import * as React from 'react';
import Raven from 'raven-js';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import EnrollmentValidationResultsSummary from 'flow-doctor-lib/components/diagnosis/non-enrollment/validation/summaries/EnrollmentValidationResultsSummary';
import usageTracker from 'flow-doctor-lib/usageTracker';
import useNonEnrollmentDiagnosis from 'flow-doctor-lib/hooks/useNonEnrollmentDiagnosis';
import EnrollmentValidationChecksView from 'flow-doctor-lib/components/diagnosis/non-enrollment/validation/EnrollmentValidationChecksView';
import SelectableEnrollmentTimelineItem from 'flow-doctor-lib/components/timeline/SelectableEnrollmentTimelineItem';
import UISection from 'UIComponents/section/UISection';
import { mapTriggerAndEnrollmentChecksToValidationCheckResults } from 'flow-doctor-lib/utils/nonEnrollmentDiagnosis';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * This is the top-level component that hosts all of the "Why didn't this object enroll" diagnositc information.
 * Valid trigger checks and enrollment checks are both combined into a common interface, and then decomposed further
 * down in the subtree where they can be displayed via labels and explanation components depending on what data they hold.
 */
const TimelineItemDiagnosisStep = ({
  flowId,
  objectId,
  objectTypeId,
  timelineItem
}) => {
  const {
    data,
    loading,
    error
  } = useNonEnrollmentDiagnosis({
    flowId,
    objectId,
    timelineItem
  });
  React.useEffect(() => {
    usageTracker.track('pageAction', {
      actionType: 'VIEW_NON_ENROLLMENT_DIAGNOSIS',
      objectTypeId
    });
  }, [objectTypeId]);

  // Surface any errors so they don't get swallowed by DFC
  React.useEffect(() => {
    if (error) {
      Raven.captureMessage('Error while running enrollment checks', {
        extra: {
          error
        }
      });
      console.error(error);
    }
  }, [error]);
  if (!error && (loading || !data)) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      layout: "centered"
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(UIErrorMessage, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.nonEnrollment.diagnosis.error.title"
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.nonEnrollment.diagnosis.error.description"
      })
    });
  }
  const validationChecksByStatus = mapTriggerAndEnrollmentChecksToValidationCheckResults(data.triggerValidationResults, data.validationResults);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(EnrollmentValidationResultsSummary, {
      objectId: objectId,
      shouldEnroll: data.shouldEnroll
      // NOTE: Need this cast because we customize certain types in automation-platform-service-types
      // that we don't in flow-doctor-service-types. Ideally we'd have a single source of truth for
      // these overrides, but until then...
      ,
      flowRevision: data.flowRevision,
      validationChecksByStatus: validationChecksByStatus
    }), /*#__PURE__*/_jsx(UISection, {
      className: "m-bottom-6",
      children: /*#__PURE__*/_jsx(SelectableEnrollmentTimelineItem, {
        flowId: flowId,
        objectTypeId: objectTypeId,
        logItem: timelineItem
      })
    }), /*#__PURE__*/_jsx(EnrollmentValidationChecksView, {
      validationChecksByStatus: validationChecksByStatus
      // NOTE: Need this cast because we customize certain types in automation-platform-service-types
      // that we don't in flow-doctor-service-types. Ideally we'd have a single source of truth for
      // these overrides, but until then...
      ,
      flowRevision: data.flowRevision,
      objectId: objectId,
      timelineItem: timelineItem
    })]
  });
};
export default TimelineItemDiagnosisStep;